import { server } from '../core/server/axios';
import { analyticsConfig, analyticsQuery } from './AnalyticsClient';

/**
 * Казначейские лимиты — сервис ECASH Analytics (не бэк ECASH!): доска лимитов, динамика
 * залежалости, отдача капитала, симуляции, базовый лимит. Адрес — `ANALYTICS_URL`, токен тот же,
 * что у бэка ECASH. Все расчёты ведёт сервер: фронт ничего не пересчитывает за него.
 *
 * Бэк отдаёт `_calculatedAt` / `_pendingRecompute` вместе с кешем и пересобирает его в фоне:
 * `cacheStatus` = building — норма почти всё время, а не «данных нет».
 */

const LIMITS = '/api/treasury-limits';

/** Таймаут «лёгких» запросов; пересборка доски и симуляции считаются на порядки дольше */
const SUMMARY_TIMEOUT_MS = 8_000;
const CALCULATE_TIMEOUT_MS = 300_000;
const CAPITAL_TIMEOUT_MS = 360_000;



/**
 * Task #159 — backend now stamps every freshness-aware response with
 * `_calculatedAt` (ISO timestamp of the source calc) and `_pendingRecompute`
 * (true while the dirty-queue worker is recalculating that scope). Both
 * fields are additive; downstream code may ignore them safely.
 */
export interface FreshnessMeta {
    _calculatedAt?: string;
    _pendingRecompute?: boolean;
}

/** One coverage currency inside the open FX position. */
export interface FxPositionCurrency {
    currency: string;
    qty: number;
    kzt: number;
    /** null when the USD/KZT rate is unavailable — never 0. */
    usd: number | null;
    sharePct: number | null;
}

/**
 * Открытая валютная позиция сети — сырые остатки в валютах покрытия минус
 * валютный долг, БЕЗ хеджката по валютам и БЕЗ КПН.
 *
 * ⚠️ Это НЕ `effectiveGapUsd` («разрыв хеджирования»): тот считается от факта
 * ПОСЛЕ хеджката и ПОСЛЕ КПН против цели «обязательства × коэффициент стратегии».
 * Две разные величины; `hedgeFactUsd` здесь — мостик между ними.
 *
 * Считается на бэкенде тем же кодом, что посуточный ряд на «Соблюдении лимитов»
 * (`src/fx_position_history.py`). Фронт ничего не пересчитывает: своё определение
 * позиции в браузере разошлось бы с тем экраном молча.
 */
export interface FxPositionLive {
    available: boolean;
    /** Почему позиции нет. Показывать вместо нулей. */
    reason?: string;
    asOf?: string | null;
    /** Валюты покрытия, входящие в позицию (RUB и экзотика в неё не входят). */
    currencies?: string[];
    assetsKzt?: number;
    assetsUsd?: number | null;
    /** Активы с хеджкатом по валютам — сводится с «Фактом USD» базового лимита. */
    hedgeFactUsd?: number | null;
    liabilitiesUsd?: number | null;
    netUsd?: number | null;
    netKzt?: number | null;
    coveragePct?: number | null;
    usdKzt?: number | null;
    /** Активы ТОЛЬКО по кассам точек — без ручных остатков на счетах.
     *  Нужны, чтобы подпись «в кассах» на экране не включала ручные деньги. */
    cashAssetsKzt?: number;
    cashAssetsUsd?: number | null;
    /** Валюта на текущих банковских счетах — РУЧНОЙ ввод (автоматического
     *  источника не существует). Входит в активы и нетто, но НЕ в `hedgeFactUsd`. */
    accountsUsd?: number | null;
    accountsKzt?: number | null;
    accountsByCcy?: Record<string, number>;
    accountsSource?: string | null;
    accountsChangedBy?: string | null;
    accountsChangedAt?: string | null;
    accountsComment?: string | null;
    /** Строки остатка без курса НБК: отброшены, НЕ обнулены. */
    noRateRows?: number;
    rows?: number;
    byCurrency?: FxPositionCurrency[];
    note?: string;
}

export interface NetworkSummary extends FreshnessMeta {
    cacheStatus?: "cold" | "building" | "fresh" | "stale";
    totalNetworkBaseLimitUsd: number;
    usdObligations: number;
    /** Origin of usdObligations: "liabilities" (live current-month total),
     *  "treasury_app" (manual DB override) or "config" (file default). */
    usdObligationsSource?: "liabilities" | "treasury_app" | "config";
    /** Month-end (ISO date) of the live liabilities total, when source is "liabilities". */
    usdObligationsMonth?: string | null;
    baseBufferPct: number;
    strategyCoefficient: number;
    strategyName: string;
    grossCoverageUsd: number;
    grossCoveragePct: number;
    grossCoverageGapUsd: number;
    totalCoverageUsd: number;
    coveragePct: number;
    coverageGapUsd: number;
    branchCount: number;
    currencyCount: number;
    totalWorkingMinimumUsd: number;
    totalExcessUsd: number;
    networkTargetEffectiveHedgeUsd: number;
    totalTargetActualUsd: number;
    totalFactUsd: number;
    totalActualEffectiveHedgeUsd: number;
    effectiveCoveragePct: number;
    effectiveGapUsd: number;
    citRate: number;
    /** КПН-off (tax-symmetric) variant for the «с КПН / без КПН» toggle.
     *  Target is shared (never carried CIT); only the fact side differs. */
    noCit?: {
        networkTargetEffectiveHedgeUsd: number;
        totalTargetActualUsd: number;
        totalFactUsd: number;
        totalActualEffectiveHedgeUsd: number;
        effectiveCoveragePct: number;
        effectiveGapUsd: number;
    };
    /** Открытая валютная позиция; null на холодном кэше — рисовать «считается». */
    fxPosition?: FxPositionLive | null;
}

export interface CitySummary extends FreshnessMeta {
    cityId: number;
    cityName: string;
    branchCount: number;
    grossCoverageUsd: number;
    grossCoveragePct: number;
    totalCoverageUsd: number;
    coveragePct: number;
    totalWorkingMinimumUsd: number;
}

export interface CoverageSummary extends FreshnessMeta {
    currencyCode: string;
    haircutPct: number;
    fxRateToUsd: number;
    grossBalance: number;
    grossBalanceUsd: number;
    totalAvailable: number;
    totalAvailableUsd: number;
    contributionPct: number;
}

export interface BranchLimitResult extends FreshnessMeta {
    branchId: number;
    branchName: string;
    cityId: number;
    /** City display name (e.g. «Алматы»/«Астана»/«Актобе»); preferred over cityId-based mapping. */
    cityName?: string;
    /**
     * Franchise point (Abu Dhabi, Almaty Mall) — shown in the grid but excluded
     * from transfer recommendations and the create-transfer Откуда/Куда selects.
     */
    isFranchise?: boolean;
    ownershipPct: number;
    assetCode: string;
    assetType: "currency" | "kzt" | "gold";
    avgDailyBuy: number;
    avgDailySell: number;
    avgDailyNetOutflow: number;
    avgClosingBalance: number;
    calendarDays: number;
    activeDays: number;
    calculatedMinimumLimit: number;
    kztWithdrawalReserve: number;
    treasuryLimit: number | null;
    workingMinimumLimit: number;
    /**
     * Task #178 — null when neither `branch_asset_current_balances` nor
     * the engine snapshot has a value for this (branch, asset). Renders
     * as em-dash; KPI / coverage math MUST treat null as "skip", never 0.
     * Real `0` means a row exists with balance 0.
     */
    currentBalance: number | null;
    /** "live" | "engine_snapshot" | "missing" — diagnostic from backend. */
    currentBalanceSource?: "live" | "engine_snapshot" | "missing";
    baseAllocationBalance: number;
    /** КПН-off coverage base allocation for the matrix «без КПН» toggle. */
    baseAllocationBalanceNoCit?: number;
    availableForCoverage: number;
    excessBalance: number;
    limitSource: "calculated" | "manual" | "hybrid";
    insufficientData: boolean;
    hasBalanceHistory: boolean;
    /**
     * Task #172 — categorical balance-history signal from the engine.
     * Authoritative source for the "Баланс (нет истории)" badge. The
     * legacy `hasBalanceHistory` boolean is kept for back-compat consumers
     * but MUST NOT be used to drive the no_history badge anymore.
     */
    historyStatus?: "strong_balance_history" | "weak_balance_history" | "activity_only" | "no_history";
    isCoverage: boolean;
    stalenessDays: number | null;
    /**
     * stalenessDays is a LOWER bound: the known purchases ran out before the balance
     * did, so the uncovered tail was charged the oldest age we saw. Printed with «≥».
     */
    stalenessPartial?: boolean;
    /**
     * No purchase of this currency at this branch exists on any horizon — the balance
     * arrived some other way. «Нет данных», which must never read as «свежая».
     */
    stalenessNoPurchases?: boolean;
    /** Share of the balance the known purchases explain, 0..100. */
    stalenessCoveragePct?: number | null;
    rotPct: number | null;
    wacKzt: number | null;
    nbkRateKzt: number | null;
    turnoverKzt60d: number;
    /** Today's buy volume in local currency (KZT for forex, pieces for gold). Phase-2E. */
    intradayBuyKzt: number;
    intradaySellKzt: number;
    intradayBuyQty: number;
    intradaySellQty: number;
    /** Today's confirmed deal counts — shown next to the average rate on the card. */
    intradayBuyCount: number;
    intradaySellCount: number;
    /** Объём продаж (qty в валюте/штуках) за последние 30 дней. «Продано 30д». */
    sold30dQty: number;
    /** Объём покупок (qty в валюте/штуках) за последние 30 дней. «Куплено 30д». */
    bought30dQty: number;
}

export interface BranchSummary extends FreshnessMeta {
    branchId: number;
    branchName: string;
    cityId: number;
    cityName: string;
    ownershipPct: number;
    capitalKzt: number;
    totalBalanceKzt: number;
    weight: number;
    branchBaseLimitUsd: number;
    effectiveHedgeCoeff: number;
    factUsd: number;
    branchTargetEffectiveHedgeUsd: number;
    branchTargetActualUsd: number;
    branchActualEffectiveHedgeUsd: number;
    deviationUsd: number;
    effectiveGapUsd: number;
    isHedgeable: boolean;
    /** КПН-off variant of the CIT-sensitive columns for the «без КПН» toggle. */
    noCit?: {
        effectiveHedgeCoeff: number;
        branchTargetActualUsd: number;
        branchActualEffectiveHedgeUsd: number;
        deviationUsd: number;
        effectiveGapUsd: number;
    };
}

export interface CalculateResponse {
    ok: boolean;
    networkSummary: NetworkSummary;
    citySummaries: CitySummary[];
    coverageSummary: CoverageSummary[];
    branchSummaries: BranchSummary[];
    branchAssetCount: number;
    computedAt: string;
}

/** Lightweight ack from the now-non-blocking POST /calculate. The body is just a
 *  signal that a background rebuild was kicked off; the client polls the summary
 *  endpoints and watches `cacheStatus` flip to "fresh". */
export interface CalculateAck {
    ok: boolean;
    status: "building" | "ok" | "error";
    cacheStatus: "cold" | "building" | "fresh" | "stale";
    buildStarted?: boolean;
    _pendingRecompute?: boolean;
    computedAt: string;
}

export interface DemandMetrics {
    assetCode: string;
    avgDailyBuy: number;
    avgDailySell: number;
    avgDailyNetOutflow: number;
    avgClosingBalance: number;
    calendarDays: number;
    activeDays: number;
    insufficientData: boolean;
}

/**
 * Per-branch net KZT needed to cover all limits across currencies.
 * `netMinKzt` nets balance−workingMin over every non-gold currency (KZT surplus
 * offsets e.g. a USD deficit); `netBaseKzt` does the same against the base limit.
 * Positive ⇒ surplus ₸; negative ⇒ ₸ that must be injected. Currencies without
 * an NBK rate are skipped and listed in `unconvertedCurrencies`.
 */
export interface BranchNetCoverage {
    branchId: number;
    branchName: string;
    netMinKzt: number;
    netBaseKzt: number;
    unconvertedCurrencies: string[];
}

/* ------------------------------------------------------------------------ */
/*  Capital-injection what-if simulation («Симуляция капитала»)            */
/* ------------------------------------------------------------------------ */

/** One manual input: USD capital injected (+) or withdrawn (−) into a branch. */
export interface CapitalInjection {
    branch: string;
    usd: number;
}

/** Per-branch simulated result. baseLimitUsd is the redistributed target
 * (TNET × weight); net* are the simulated «до базового» / «над минимумом» in ₸. */
export interface SimBranchRow {
    branchId: number;
    branchName: string;
    /** Капитал = non-gold KZT баланс точки (база для веса). Reflects the injection. */
    capitalKzt: number;
    /** 30-дн оборот / торговый доход (капитал-независимы). ROI/нагрузка считаются
     *  против capitalKzt этого вызова: roi=income/capital×100, load=turnover/capital. */
    turnoverKzt: number;
    tradingIncomeKzt: number;
    roiPct: number | null;
    loadRatio: number | null;
    baseLimitUsd: number;
    factUsd: number;
    weight: number;
    netBaseKzt: number;
    netMinKzt: number;
    unconvertedCurrencies: string[];
    injectedUsd: number;
}

export interface SimulateResponse {
    /** "ok" = result ready; "warming" = baseline inputs still building, retry shortly;
     *  "error" = build failed (see warnings). */
    status?: "ok" | "warming" | "error";
    network: {
        targetEffectiveHedgeUsd: number;
        factUsd: number;
        coverageEffPct: number;
        hedgeGapUsd: number;
        totalNetworkBaseLimitUsd: number;
        totalNetBaseKzt: number;
        totalNetMinKzt: number;
        usdKzt: number;
    };
    branches: SimBranchRow[];
    warnings: string[];
    appliedInjectionsUsd: Record<string, number>;
}

/** POST per-branch USD deltas → simulated base limit / net coverage. Read-only;
 * the heavy build path can take a while, so it reuses the long calculate timeout. */

/** Task #159 — small per-branch summary, lets pages render KPIs/hedge table
 * before the heavy /branch-limits grid arrives. */

export interface TrendPoint {
    date: string;
    stalenessDays: number | null;
    rotPct: number | null;
    balance: number;
}

/* ------------------------------------------------------------------------ */
/*  Task #183 — Aging Dynamics («Динамика залежалости») client wrappers     */
/* ------------------------------------------------------------------------ */

export type AgingPointSource = "snapshot" | "fallback" | "missing";
export type AgingStatus = "normal" | "risk" | "critical" | "unknown";

export interface AgingPoint {
    date: string;
    value: number | null;
    source: AgingPointSource;
    /** Backend-computed day-over-day delta vs the previous point. */
    dod: number | null;
}

export interface AgingRow {
    branchId: number;
    branchName: string;
    cityId: number;
    cityName: string | null;
    assetCode: string;
    today: number | null;
    todayDate: string;
    todaySource: AgingPointSource;
    yesterday: number | null;
    yesterdayDate: string;
    yesterdaySource: AgingPointSource;
    delta: number | null;
    mean7: number | null;
    deltaMean7: number | null;
    max30: number | null;
    status: AgingStatus;
    series: AgingPoint[];
    seriesAverage: number | null;
    rowHistoryAvailableFrom: string | null;
    hasFallback: boolean;
}

export interface AgingKpis {
    avgNetwork: number | null;
    problemCount: number;
    worst: { branchId: number; branchName: string; assetCode: string; value: number } | null;
    biggestWorsening: { branchId: number; branchName: string; assetCode: string; delta: number } | null;
    biggestImprovement: { branchId: number; branchName: string; assetCode: string; delta: number } | null;
}

export interface AgingThresholds {
    normaDays: number;
    riskDays: number;
}

export interface AgingDynamicsResponse {
    asOfDate: string;
    startDate: string;
    endDate: string;
    historyAvailableFrom: string | null;
    rows: AgingRow[];
    kpis: AgingKpis;
    thresholds: AgingThresholds;
    pendingRecompute: boolean;
}

export interface AgingDynamicsParams {
    startDate: string;
    endDate: string;
    cityId?: number | null;
    branchId?: number | null;
    assetCode?: string | null;
    problematicOnly?: boolean;
    status?: "all" | "normal" | "risk" | "critical";
}

/** One day of the «Продано 30д» hover chart. */
export interface SalesPoint {
    date: string;
    sellQty: number;
    buyQty: number;
}

export interface SalesDynamicsResponse {
    status: "ok" | "error" | "db_unavailable";
    error?: string | null;
    startDate: string;
    endDate: string;
    branchId: number | null;
    branchName: string | null;
    assetCode: string | null;
    points: SalesPoint[];
    totalQty: number;
    avgDailyQty: number;
    totalBuyQty: number;
    avgDailyBuyQty: number;
}

export interface SalesDynamicsParams {
    startDate: string;
    endDate: string;
    branchId: number;
    assetCode: string;
}

/* «Перемещено 30д» — net inter-branch movement via the collector (ClientId=1). */

export interface MovedPoint {
    date: string;
    netQty: number;
    inQty: number;
    outQty: number;
}

export interface MovedDynamicsResponse {
    status: "ok" | "error" | "db_unavailable";
    error?: string | null;
    startDate: string;
    endDate: string;
    branchId: number | null;
    branchName: string | null;
    assetCode: string | null;
    points: MovedPoint[];
    netQty: number;
    inQty: number;
    outQty: number;
}

export interface MovedMapResponse {
    /** Keyed by `${branchName}|${assetCode}` → net moved qty (in − out). */
    map: Record<string, number>;
    endDate: string;
    days: number;
    status?: string;
    error?: string;
}

/* «Отдача / Поток» — capital efficiency (return lens) + supply-constraint flag.
     Side view; the limits engine is untouched. See src/treasury_capital_efficiency.py. */

export type SupplySeverity = "red" | "amber" | "none";

export interface CapitalEfficiencyRow {
    branch: string;
    city: string | null;
    assetCode: string;
    assetType: "currency" | "kzt";
    isCoverage: boolean;
    // return lens
    rotPct: number | null;
    turnoverKzt60d: number;
    marginKzt60d: number | null;
    capitalKzt: number | null;
    returnOnCapitalPct: number | null;
    turnoverVelocity: number | null;
    stalenessDays: number | null;
    // flow / deficit lens
    currentBalance: number | null;
    workingMin: number;
    baseAlloc: number;
    sold30dQty: number;
    bought30dQty: number;
    netFlow30dQty: number;
    flowSide: "buyer" | "seller" | "flat";
    idleNative: number;
    idleKzt: number | null;
    // supply-constraint flag
    scSeverity: SupplySeverity;
    scLeanDays: number | null;
    scLeanFrac: number | null;
    scSoldoutDays: number | null;
    scHotPremium: number | null;
    scWindowDays: number | null;
}

export interface CapitalEfficiencySummary {
    cellCount: number;
    currencyCellCount: number;
    totalIdleKzt: number;
    idleKztStore: number;
    idleFxKzt: number;
    totalMarginKzt60d: number;
    supplyConstrainedRed: number;
    supplyConstrainedAmber: number;
    asOfDate: string;
}

export interface CapitalEfficiencyResponse {
    status: "ok" | "error" | "db_unavailable";
    error?: string | null;
    asOfDate: string;
    lookbackDays: number;
    city: string | null;
    rows: CapitalEfficiencyRow[];
    summary: CapitalEfficiencySummary;
    balanceSource?: string | null;
}

/* «Доходность капитала» — ROC/month (ROT × velocity) ranking + spread-cut scenario.
     Side view over capital-efficiency + posted spreads + USD-experiment elasticity.
     See src/treasury_capital_yield.py. */

export type ElasticitySource = "measured" | "carry_usd";

export interface CapitalYieldRow {
    branch: string;
    city: string | null;
    assetCode: string;
    assetType: "currency" | "kzt";
    isCoverage: boolean;
    // monthly yield block
    rotPct: number | null;
    monthlyTurnoverKzt: number;
    marginMonthlyKzt: number | null;
    capitalKzt: number | null;
    velocityMonth: number | null;
    rocMonthPct: number | null;
    stalenessDays: number | null;
    // spread baseline (s0) for the scenario
    spreadSell: number | null;
    spreadBuy: number | null;
    spreadTotal: number | null;
    spreadSource: "pair" | "branch" | "none";
    defaultElasticity: number;
    elasticitySource: ElasticitySource;
    // supply-constraint passthrough (observed velocity is a lower bound when set)
    scSeverity: SupplySeverity;
    scLeanDays: number | null;
    scLeanFrac: number | null;
    scSoldoutDays: number | null;
    scHotPremium: number | null;
    scWindowDays: number | null;
}

export interface ElasticityInfo {
    elasticity: number;
    method: "did_arc" | "fallback";
    confidence: "low";
    sBefore: number | null;
    sAfter: number | null;
    treatTurnGrowthPct: number | null;
    ctrlTurnGrowthPct: number | null;
    treatedBranches: string[];
    controlBranches: string[];
    observedSpreadLow: number | null;
    observedSpreadHigh: number | null;
    note: string;
}

export interface CapitalYieldSummary {
    cellCount: number;
    rankedCellCount: number;
    capitalWeightedRocMonthPct: number | null;
    bestRocMonthPct: number | null;
    bestCell: string | null;
    worstRocMonthPct: number | null;
    worstCell: string | null;
    spreadResolvedCount: number;
    asOfDate: string;
}

export interface CapitalYieldResponse {
    status: "ok" | "error" | "db_unavailable";
    error?: string | null;
    asOfDate: string;
    lookbackDays: number;
    city: string | null;
    rows: CapitalYieldRow[];
    summary: CapitalYieldSummary;
    elasticity: ElasticityInfo | null;
    balanceSource?: string | null;
}

/* ------------------------------------------------------------------------ */
/*  Profit simulator («Симуляция дохода») — сколько продать для N ₸/мес     */
/* ------------------------------------------------------------------------ */

/** Layer A row: unit economics of earning 1 млн ₸ on one (branch × currency). */
export interface ProfitSimUnitRow {
    branch: string | null;
    asset: string;
    rotPct: number | null;
    rotSource: "engine" | "override" | "none";
    sellRateKzt: number | null;
    sellRateSource: "intraday" | "derived" | "wac" | null;
    /** Board display convention: UZS/KRW rate shown ×100, AMD ×10. */
    rateDisplayMultiplier: number;
    monthlyTurnoverKzt: number;
    sold30dQty: number;
    stalenessDays: number | null;
    balanceQty: number | null;
    requiredTurnoverKzt: number | null;
    requiredQty: number | null;
    requiredCapitalKzt: number | null;
    shareOfCurrentPct: number | null;
    unreliable: boolean;
    /** Monthly turnover below the reliability threshold — ROT is deal noise. */
    lowVolume?: boolean;
    light: "green" | "amber" | "red" | "none";
    note: string | null;
}

/** Layer B allocation leg: sell this much of this currency (at this branch). */
export interface ProfitSimAllocation {
    branch: string | null;
    asset: string;
    rotPct: number | null;
    rotSource: string;
    profitKzt: number;
    turnoverKzt: number;
    qty: number | null;
    sellRateKzt: number | null;
    rateDisplayMultiplier: number;
    utilizationPct: number | null;
    capitalKzt: number | null;
    stalenessDays: number | null;
}

/** «Что делать»: one action per currency — mix legs rolled up, branches as drill-down. */
export interface ProfitSimPlanAction {
    asset: string;
    profitKzt: number;
    turnoverKzt: number;
    qty: number | null;
    sellRateKzt: number | null;
    rateDisplayMultiplier: number;
    capitalKzt: number;
    /** Part of the legs had no staleness data — capitalKzt is the known part only. */
    capitalUnknown: boolean;
    sharePct: number | null;
    branches: { branch: string | null; qty: number | null; profitKzt: number }[];
}

export interface ProfitSimPlan {
    targetKzt: number;
    achievedKzt: number;
    actions: ProfitSimPlanAction[];
    small: { count: number; profitKzt: number; actions: ProfitSimPlanAction[] };
    coveredKzt: number;
    coveredPct: number | null;
    smallSharePct: number;
}

export interface ProfitSimMix {
    targetKzt: number;
    achievedKzt: number;
    achievable: boolean;
    maxProfitKzt: number | null;
    demandCapOn: boolean;
    verdict: string;
    allocations: ProfitSimAllocation[];
    skipped: { branch: string | null; asset: string; reason: string }[];
    requiredCapitalKzt: number;
    capitalPartiallyUnknown: boolean;
    capital: {
        requiredKzt: number;
        availableKzt: number | null;
        ok: boolean | null;
        gapKzt: number | null;
        partiallyUnknown: boolean;
    };
}

export interface ProfitSimResponse {
    status: "ok" | "warming" | "error";
    error?: string;
    asOfDate?: string | null;
    scope?: { branch: string | null; network: boolean };
    params?: {
        targetKzt: number;
        unitTargetKzt: number;
        basisRequested: string;
        basisApplied: string;
        basisAvailable: { fact: boolean; honest: boolean };
        demandCapOn: boolean;
        lookbackDays: number;
        rotOverrides: Record<string, number>;
        excludeAssets: string[];
        includeSmall?: boolean;
        minReliableTurnoverKzt?: number;
    };
    branches: string[];
    unitEconomics: ProfitSimUnitRow[];
    mix: ProfitSimMix | null;
    plan?: ProfitSimPlan | null;
    notes: string[];
}

export interface ProfitSimParams {
    branch?: string | null;
    targetKzt: number;
    basis?: "fact" | "honest";
    demandCapOn?: boolean;
    excludeAssets?: string[];
    rotOverrides?: Record<string, number>;
    /** Return low-turnover cells (noisy ROT) to the mix; default off. */
    includeSmall?: boolean;
}

export interface AssetDisplayOrderItem {
    assetCode: string;
    displayOrder: number;
}

export type CellStatus = "critical" | "warning" | "base_deficit" | "normal" | "excess" | "no_history" | "inactive" | "no_balance";


// ── Настройки лимитов ─────────────────────────────────────────────────────────

export interface TreasurySettingsData {
    id: number;
    usdObligations: string;
    baseBufferPct: string;
    activeStrategyId: number | null;
    /** Окно истории для Min Limit, дней (7..365); без записи сервер отдаёт 60 */
    minLimitLookbackDays?: number | null;
}

export interface KztSettingsData {
    id: number;
    buyCoverDays: number;
    netCoverDays: number;
    floor: string;
}

export interface GoldSettingsData {
    id: number;
    coverDays: number;
}

export interface LimitsSettingsResponse {
    treasury: TreasurySettingsData;
    kzt: KztSettingsData;
    gold: GoldSettingsData;
}

export interface Strategy {
    id: number;
    name: string;
    coefficient: string;
    isActive: boolean;
    description: string | null;
}

export interface CoverageCurrency {
    id: number;
    currencyCode: string;
    haircutPct: string;
    isActive: boolean;
    currencyName: string | null;
}

export interface GoldItem {
    id: number;
    code: string;
    weightGrams: string;
    isLiquid: boolean;
    isActive: boolean;
}

/** Валюта на текущих банковских счетах — ручной ввод, автоматического источника нет */
export interface BankAccountsData {
    balances: Record<string, number>;
    currencies: string[];
    source?: string | null;
    changedBy?: string | null;
    changedAt?: string | null;
    comment?: string | null;
}

export interface CoverDaysBranch {
    name: string;
    isFranchise: boolean;
}

export interface CoverDaysCity {
    name: string;
    id: number;
    branches: CoverDaysBranch[];
}

export interface CoverDaysRules {
    global: Record<string, number>;
    city: Record<string, Record<string, number>>;
    branch: Record<string, Record<string, number>>;
}

export interface CoverDaysResponse {
    assets: string[];
    defaults: Record<string, number>;
    cities: CoverDaysCity[];
    rules: CoverDaysRules;
    source: string;
    changedBy: string | null;
    changedAt: string | null;
    min: number;
    max: number;
}

/** Строка журнала изменений в том виде, как её хранит бэк (snake_case) */
export type AuditLogRawRow = Record<string, unknown>;

export interface AuditLogParams {
    pageSize?: number;
    limit?: number;
    entityType?: string;
}

// ── «Рекомендации»: план перемещений ───────────────────────────────────────────

export type TransferPriority = 'critical' | 'recommended' | 'optional';

export interface TransferRec {
    currency: string;
    from_point: string;
    from_city: string;
    to_point: string;
    to_city: string;
    amount: number;
    priority: TransferPriority;
    score: number;
    reason: string;
}

export interface TransferPlan {
    within_city: TransferRec[];
    cross_city: TransferRec[];
}

// ── Смежные данные, которые читают вкладки лимитов ──────────────────────────────

export interface CashBalanceItem {
    currency: string;
    amount: number;
    balanceKzt: number;
    /** WAC франшизы (в конвенции НБК за 100/10) */
    currentWac?: number | null;
    currentBuy?: number | null;
    currentSell?: number | null;
}

export interface CashGoldItem {
    denomination: string;
    qty: number;
    wacKzt: number;
    totalKzt: number;
}

export interface CashBalanceBranch {
    branchId: number | null;
    branch: string;
    city: string | null;
    balances: CashBalanceItem[];
    goldItems?: CashGoldItem[];
    goldTotalKzt?: number;
}

export interface CashBalancesResponse {
    source: string;
    asOfDate: string | null;
    quiqLive?: boolean;
    warning?: string | null;
    branches: CashBalanceBranch[];
}

/** «Маржа+»: самодостаточность точки за последние 6 полных месяцев. "new" — точке меньше 3 месяцев */
export type CoverageStatus = 'green' | 'yellow' | 'red' | 'new';

export interface MarginTargetsConsRow {
    branchId: number;
    branchName: string;
    city: string;
    isFranchise?: boolean;
    status: CoverageStatus | null;
}

export interface MarginTargetsConsResponse {
    period: { year: number; month: number; window: { from: string; to: string; months: number } };
    rows: MarginTargetsConsRow[];
}

type Opts = { signal?: AbortSignal };
type BranchLimitsParams = { branchId?: number; assetCode?: string; assetType?: string };

export class TreasuryLimitsController {
    private get = <T>(path: string, timeout: number | undefined, signal?: AbortSignal): Promise<T> => (
        server.get(`${LIMITS}${path}`, analyticsConfig({ signal, timeout }))
    );

    // ── Доска и сводки ─────────────────────────────────────────────────────────

    /** Запускает фоновую пересборку и сразу возвращается; готовность смотрят по `cacheStatus` сводок */
    calculateLimits(): Promise<CalculateAck> {
        return server.post(`${LIMITS}/calculate`, undefined, analyticsConfig({ timeout: CALCULATE_TIMEOUT_MS }));
    }

    fetchNetworkSummary({ signal }: Opts = {}) { return this.get<NetworkSummary>('/network-summary', SUMMARY_TIMEOUT_MS, signal); }
    fetchCitySummaries({ signal }: Opts = {}) { return this.get<CitySummary[]>('/city-summary', SUMMARY_TIMEOUT_MS, signal); }
    fetchCoverageSummary({ signal }: Opts = {}) { return this.get<CoverageSummary[]>('/coverage-summary', SUMMARY_TIMEOUT_MS, signal); }
    fetchBranchNetCoverage({ signal }: Opts = {}) { return this.get<BranchNetCoverage[]>('/branch-net-coverage', SUMMARY_TIMEOUT_MS, signal); }
    fetchBranchSummaries({ signal }: Opts = {}) { return this.get<BranchSummary[]>('/branch-summary', SUMMARY_TIMEOUT_MS, signal); }

    fetchBranchLimits(params?: BranchLimitsParams, { signal }: Opts = {}) {
        return this.get<BranchLimitResult[]>(
            `/branch-limits${analyticsQuery({ branchId: params?.branchId, assetCode: params?.assetCode, assetType: params?.assetType })}`,
            SUMMARY_TIMEOUT_MS,
            signal,
        );
    }

    fetchTrend(assetCode: string, branchId?: number | null, cityId?: number | null, { signal }: Opts = {}) {
        return this.get<TrendPoint[]>(
            `/trend${analyticsQuery({ assetCode, branchId: branchId ?? undefined, cityId: cityId ?? undefined })}`,
            SUMMARY_TIMEOUT_MS,
            signal,
        );
    }

    fetchBranchMetrics(branchId: number, { signal }: Opts = {}) {
        return this.get<DemandMetrics[]>(`/branch-metrics/${branchId}`, SUMMARY_TIMEOUT_MS, signal);
    }

    // ── Динамика ────────────────────────────────────────────────────────────────

    fetchAgingDynamics(params: AgingDynamicsParams, { signal }: Opts = {}) {
        return this.get<AgingDynamicsResponse>(
            `/aging-dynamics${analyticsQuery({
                startDate: params.startDate,
                endDate: params.endDate,
                cityId: params.cityId ?? undefined,
                branchId: params.branchId ?? undefined,
                assetCode: params.assetCode,
                problematicOnly: params.problematicOnly ? '1' : undefined,
                status: params.status && params.status !== 'all' ? params.status : undefined,
            })}`,
            SUMMARY_TIMEOUT_MS,
            signal,
        );
    }

    fetchSalesDynamics(params: SalesDynamicsParams, { signal }: Opts = {}) {
        return this.get<SalesDynamicsResponse>(`/sales-dynamics${analyticsQuery({ ...params })}`, SUMMARY_TIMEOUT_MS, signal);
    }

    fetchMovedDynamics(params: SalesDynamicsParams, { signal }: Opts = {}) {
        return this.get<MovedDynamicsResponse>(`/moved-dynamics${analyticsQuery({ ...params })}`, SUMMARY_TIMEOUT_MS, signal);
    }

    /** Чистое перемещение через инкассатора за `days` дней по ключу `точка|валюта` */
    fetchMovedMap(days = 30, { signal }: Opts = {}) {
        return this.get<MovedMapResponse>(`/moved-map?days=${days}`, SUMMARY_TIMEOUT_MS, signal);
    }

    // ── Отдача капитала и симуляции ─────────────────────────────────────────────

    /** Берёт живую доску лимитов + 60 дней операций: на холодном кеше считается минуты */
    fetchCapitalEfficiency(params?: { city?: string | null; selectedDate?: string; lookbackDays?: number }, { signal }: Opts = {}) {
        return this.get<CapitalEfficiencyResponse>(
            `/capital-efficiency${analyticsQuery({ city: params?.city, selectedDate: params?.selectedDate, lookbackDays: params?.lookbackDays })}`,
            CAPITAL_TIMEOUT_MS,
            signal,
        );
    }

    fetchCapitalYield(params?: { city?: string | null; selectedDate?: string; lookbackDays?: number }, { signal }: Opts = {}) {
        return this.get<CapitalYieldResponse>(
            `/capital-yield${analyticsQuery({ city: params?.city, selectedDate: params?.selectedDate, lookbackDays: params?.lookbackDays })}`,
            CAPITAL_TIMEOUT_MS,
            signal,
        );
    }

    /** Только чтение; тяжёлая сборка, поэтому длинный таймаут */
    simulateCapital(injections: CapitalInjection[], { signal }: Opts = {}): Promise<SimulateResponse> {
        return server.post(`${LIMITS}/simulate`, { injections }, analyticsConfig({ signal, timeout: CALCULATE_TIMEOUT_MS }));
    }

    fetchProfitSim(params: ProfitSimParams, { signal }: Opts = {}) {
        return this.get<ProfitSimResponse>(
            `/profit-sim${analyticsQuery({
                branch: params.branch,
                target: params.targetKzt,
                basis: params.basis,
                demandCap: params.demandCapOn === false ? '0' : '1',
                includeSmall: params.includeSmall ? '1' : undefined,
                exclude: params.excludeAssets?.length ? params.excludeAssets.join(',') : undefined,
                rotOverrides: params.rotOverrides && Object.keys(params.rotOverrides).length ? JSON.stringify(params.rotOverrides) : undefined,
            })}`,
            CALCULATE_TIMEOUT_MS,
            signal,
        );
    }

    // ── Правки лимитов ──────────────────────────────────────────────────────────

    setTreasuryLimit(branchId: number, assetCode: string, treasuryLimit: number | null, comment?: string | null): Promise<{ ok: boolean }> {
        return server.put(`${LIMITS}/treasury-limit`, { branchId, assetCode, treasuryLimit, comment: comment ?? '' }, analyticsConfig());
    }

    setKztWithdrawalReserve(branchId: number, reserve: number): Promise<{ ok: boolean }> {
        return server.put(`${LIMITS}/kzt-withdrawal-reserve`, { branchId, reserve }, analyticsConfig());
    }

    fetchAssetDisplayOrder({ signal }: Opts = {}) { return this.get<AssetDisplayOrderItem[]>('/asset-display-order', SUMMARY_TIMEOUT_MS, signal); }

    saveAssetDisplayOrder(items: AssetDisplayOrderItem[]): Promise<AssetDisplayOrderItem[]> {
        return server.put(`${LIMITS}/asset-display-order`, items, analyticsConfig());
    }

    // ── Настройки ───────────────────────────────────────────────────────────────

    fetchSettings({ signal }: Opts = {}) { return this.get<LimitsSettingsResponse>('/settings', undefined, signal); }
    fetchStrategies({ signal }: Opts = {}) { return this.get<Strategy[]>('/strategies', undefined, signal); }
    fetchCoverageCurrencies({ signal }: Opts = {}) { return this.get<CoverageCurrency[]>('/coverage-currencies', undefined, signal); }
    fetchGoldItems({ signal }: Opts = {}) { return this.get<GoldItem[]>('/gold-items', undefined, signal); }
    fetchBankAccounts({ signal }: Opts = {}) { return this.get<BankAccountsData>('/settings/bank-accounts', undefined, signal); }
    fetchCoverDays({ signal }: Opts = {}) { return this.get<CoverDaysResponse>('/settings/cover-days', undefined, signal); }

    saveTreasurySettings(body: { usdObligations?: string; baseBufferPct?: string; activeStrategyId: number | null; minLimitLookbackDays?: number }) {
        return server.put(`${LIMITS}/settings/treasury`, body, analyticsConfig());
    }

    saveKztSettings(floor: string) { return server.put(`${LIMITS}/settings/kzt`, { floor }, analyticsConfig()); }
    saveBankAccounts(balances: Record<string, number>) { return server.put(`${LIMITS}/settings/bank-accounts`, { balances }, analyticsConfig()); }
    saveCoverDays(rules: CoverDaysRules) { return server.put(`${LIMITS}/settings/cover-days`, rules, analyticsConfig()); }
    updateStrategy(id: number, data: Partial<Strategy>) { return server.put(`${LIMITS}/strategies/${id}`, data, analyticsConfig()); }
    updateCoverageCurrency(id: number, data: Partial<CoverageCurrency>) { return server.put(`${LIMITS}/coverage-currencies/${id}`, data, analyticsConfig()); }
    updateGoldItem(id: number, data: Partial<GoldItem>) { return server.put(`${LIMITS}/gold-items/${id}`, data, analyticsConfig()); }

    /** Журнал изменений: бэк отдаёт `{ data: { items } }`, но допускаем и голый массив */
    async fetchAuditLog(params: AuditLogParams, { signal }: Opts = {}): Promise<AuditLogRawRow[]> {
        const json = await this.get<unknown>(`/audit-log${analyticsQuery({ ...params })}`, undefined, signal);

        if (Array.isArray(json)) {
            return json as AuditLogRawRow[];
        }

        return ((json as { data?: { items?: AuditLogRawRow[] } })?.data?.items) ?? [];
    }

    async fetchTransferPlan({ signal }: Opts = {}): Promise<TransferPlan> {
        const json = await this.get<{ data: TransferPlan; status: string }>('/transfer-plan', undefined, signal);

        return json.data;
    }

    // ── Данные соседних модулей ─────────────────────────────────────────────────

    /** Остатки по точкам — тот же источник, что вкладка «Остатки» */
    fetchCashBalances({ signal }: Opts = {}): Promise<CashBalancesResponse> {
        return server.get('/api/treasury/cash-balances', analyticsConfig({ signal }));
    }

    fetchMarginTargets(year: number, month: number, { signal }: Opts = {}): Promise<MarginTargetsConsResponse> {
        return server.get(`/api/analytics/margin-targets-consolidated${analyticsQuery({ year, month })}`, analyticsConfig({ signal }));
    }
}
